import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from './api';

const AuthContext = createContext();

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn(`AuthContext: failed to parse localStorage key "${key}"`, err);
    return null;
  }
};

const writeJSON = (key, value) => {
  if (value === undefined) return;
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (err) {
    console.warn(`AuthContext: failed to persist localStorage key "${key}"`, err);
  }
};

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem('token');
    const user = readJSON('user');
    return { token, user };
  });
  const [profile, setProfile] = useState(() => readJSON('profile'));
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [invoiceCache, setInvoiceCache] = useState(() => readJSON('invoiceCache') || {});
  const profileRequestRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('profile');
    localStorage.removeItem('invoiceCache');
    setAuth({ token: null, user: null });
    setProfile(null);
    setProfileError(null);
    setInvoiceCache({});
    profileRequestRef.current = null;
  }, []);

  const refreshProfile = useCallback(
    async (tokenOverride, { force = false } = {}) => {
      const tokenToUse = typeof tokenOverride === 'string' ? tokenOverride : auth.token;
      if (!tokenToUse) {
        setProfile(null);
        writeJSON('profile', null);
        return null;
      }

      if (!force && profileRequestRef.current) {
        return profileRequestRef.current;
      }

      const controller = new AbortController();
      const requestPromise = (async () => {
        setProfileLoading(true);
        setProfileError(null);
        try {
          const response = await apiFetch('/api/auth/me/', {
            headers: { Authorization: `Token ${tokenToUse}` },
            signal: controller.signal,
          });

          if (!response.ok) {
            if (response.status === 401) {
              logout();
              return null;
            }
            const detail = await response.text().catch(() => 'Failed to load profile');
            throw new Error(detail || 'Failed to load profile');
          }

          const data = await response.json();
          setProfile(data);
          writeJSON('profile', data);
          return data;
        } catch (err) {
          if (err.name === 'AbortError') {
            return null;
          }
          setProfileError(err.message || 'Failed to load profile');
          throw err;
        } finally {
          setProfileLoading(false);
          profileRequestRef.current = null;
        }
      })();

      profileRequestRef.current = requestPromise;
      return requestPromise;
    },
    [auth.token, logout]
  );

  useEffect(() => {
    if (auth.token) {
      refreshProfile(auth.token).catch(() => {});
    } else {
      setProfile(null);
      writeJSON('profile', null);
    }
  }, [auth.token, refreshProfile]);

  const login = useCallback(
    (token, user = null) => {
      localStorage.setItem('token', token);
      if (user) {
        writeJSON('user', user);
      }
      setAuth({ token, user });
      if (user && user.profile) {
        setProfile(user.profile);
        writeJSON('profile', user.profile);
      }
      refreshProfile(token, { force: true }).catch(() => {});
    },
    [refreshProfile]
  );

  const rememberInvoice = useCallback((invoice) => {
    if (!invoice || !invoice.external_id) return;
    setInvoiceCache((prev) => {
      const next = { ...prev, [invoice.external_id]: invoice };
      writeJSON('invoiceCache', next);
      return next;
    });
  }, []);

  const clearInvoiceFromCache = useCallback((invoiceId) => {
    if (!invoiceId) return;
    setInvoiceCache((prev) => {
      if (!prev[invoiceId]) return prev;
      const next = { ...prev };
      delete next[invoiceId];
      writeJSON('invoiceCache', Object.keys(next).length ? next : null);
      return next;
    });
  }, []);

  const credits = useMemo(() => {
    if (!profile) return null;
    if (typeof profile.credits === 'number') return profile.credits;
    if (profile.profile && typeof profile.profile.credits === 'number') {
      return profile.profile.credits;
    }
    return null;
  }, [profile]);

  const contextValue = useMemo(
    () => ({
      token: auth.token,
      user: auth.user,
      login,
      logout,
      profile,
      profileLoading,
      profileError,
      credits,
      refreshProfile,
      invoiceCache,
      rememberInvoice,
      clearInvoiceFromCache,
    }),
    [auth.token, auth.user, login, logout, profile, profileLoading, profileError, credits, refreshProfile, invoiceCache, rememberInvoice, clearInvoiceFromCache]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export default AuthContext;
